import { useState, useCallback, useEffect, useRef } from "react";
import PageWrapper from "./PageWrapper";
import AlertModal from "./AlertModal";
import PairwiseMoviePresentation, { REJECT_BOTH_LABEL, APPROXIMATELY_EQUAL_LABEL } from "./PairwiseMoviePresentation";
import { retryApiCall } from "../utils/retry";

import {
  startPairwiseSession,
  getNextPair,
  submitPairwiseAnswer,
  getNextExternalTestPair,
  submitExternalTestAnswer,
} from "../service/api";

const DEBUG = process.env.REACT_APP_DEBUG === 'true';
const debug = (...args) => DEBUG && console.log(...args);

const PairwiseMovieTask = ({
  userId,
  onComplete,
  mode = "personalized",
  initialSessionId = null,
  onPhaseChange = () => {},
}) => {
  const isExternal = mode === "external";
  const [showIntro, setShowIntro] = useState(!isExternal && !initialSessionId);

  const [sessionId, setSessionId] = useState(initialSessionId);
  const [trialCount, setTrialCount] = useState(0);
  const [maxTrials, setMaxTrials] = useState(null);

  const [currentPair, setCurrentPair] = useState(null); // {pairId, i, j, left, right}
  const [startTime, setStartTime] = useState(null);

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const [showCantChooseModal, setShowCantChooseModal] = useState(false);
  const [retryResponse, setRetryResponse] = useState(null);
  const submissionLockRef = useRef(false);
  const cantJudgeTriggerRef = useRef(null);
  const firstReasonRef = useRef(null);
  const restoreModalFocusRef = useRef(false);
  const initialLoadStartedRef = useRef(false);

  // Alert modal state
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: ''
  });

  const loadNextPair = useCallback(
    async (sid) => {
      setIsLoading(true);
      setLoadError(false);
      try {
        const resp = isExternal
          ? await getNextExternalTestPair({ sessionId: sid, userId })
          : await getNextPair(sid);
        
        // 🔍 DEBUG: Ver qué llega del backend
        debug('📦 Response from backend:', {
          left_id: resp.left?.imdb_tconst,
          left_poster: resp.left?.poster,
          right_id: resp.right?.imdb_tconst,
          right_poster: resp.right?.poster,
        });
        
        if (!resp.ok) {
          if (resp.phase && resp.reason === "authoritative_phase_transition") {
            onPhaseChange(resp.phase, sid);
            setIsLoading(false);
            return;
          }
          if (resp.done) {
            // No pairs left (should never happen in a 50-trial task, but handle gracefully)
            setLoadError(true);
            setIsLoading(false);
            return;
          }
          throw new Error(resp.message || "Failed to load next pair");
        }
        if (resp.done && resp.phase === "complete") {
          onComplete();
          setIsLoading(false);
          return;
        }
        if (resp.done && resp.phase && resp.phase !== "personalized_pairwise") {
          onPhaseChange(resp.phase, sid);
          setIsLoading(false);
          return;
        }

        const left = {
          id: resp.left.imdb_tconst,
          title: resp.left.title,
          posterPath: resp.left.poster,
          plot: resp.left.overview || "No description available.",
          year: resp.left.year,
          genre: resp.left.genre,
          runtime: resp.left.runtime,
        };

        const right = {
          id: resp.right.imdb_tconst,
          title: resp.right.title,
          posterPath: resp.right.poster,
          plot: resp.right.overview || "No description available.",
          year: resp.right.year,
          genre: resp.right.genre,
          runtime: resp.right.runtime,
        };
        
        debug('🎬 Movies prepared:', {
          left_posterPath: left.posterPath,
          right_posterPath: right.posterPath,
        });

        setCurrentPair({
          pairId: resp.pairId,
          i: resp.i,
          j: resp.j,
          left,
          right,
          t: resp.t,
          meta: resp.meta ?? null,
          issuedTrialId: resp.issuedTrialId,
          answerToken: resp.answerToken,
          progress: resp.progress ?? null,
        });
        if (!isExternal) {
          const authoritativeMaxTrials = Number(resp.maxTrials);
          if (!Number.isInteger(authoritativeMaxTrials) || authoritativeMaxTrials <= 0) {
            throw new Error("Invalid personalized response cap");
          }
          setMaxTrials(authoritativeMaxTrials);
        }

        setTrialCount(isExternal
          ? Math.max(0, Number(resp.progress?.current ?? 1) - 1)
          : Number(resp.t ?? 0));
        setStartTime(Date.now());
        setSubmitError(null);
        setIsLoading(false);
      } catch (err) {
        console.error("Error loading next pair:", err);
        setLoadError(true);
        setIsLoading(false);
      }
    },
    [isExternal, onComplete, onPhaseChange, userId]
  );

  // Start session once (when intro ends)
  const beginTask = useCallback(async () => {
    try {
      setIsLoading(true);
      const resp = await startPairwiseSession({ userId });
      if (!resp.ok) throw new Error(resp.message || "Failed to start pairwise session");
      setSessionId(resp.sessionId);
      if (Number.isInteger(Number(resp.maxTrials))) setMaxTrials(Number(resp.maxTrials));
      if (resp.phase && resp.phase !== "personalized_pairwise") {
        onPhaseChange(resp.phase, resp.sessionId);
        setIsLoading(false);
        return;
      }
      await loadNextPair(resp.sessionId);
    } catch (err) {
      console.error("Error starting pairwise session:", err);
      setLoadError(true);
      setIsLoading(false);
    }
  }, [userId, loadNextPair, onPhaseChange]);

  useEffect(() => {
    if (!initialSessionId || showIntro || initialLoadStartedRef.current) return;
    initialLoadStartedRef.current = true;
    loadNextPair(initialSessionId);
  }, [initialSessionId, loadNextPair, showIntro]);

  const submitResponse = useCallback(
    async (answerData) => {
      if (submissionLockRef.current || !answerData) return;
      submissionLockRef.current = true;
      setIsSubmitting(true);
      setSubmitError(null);

      try {
        const resp = await retryApiCall(() => isExternal
          ? submitExternalTestAnswer(answerData)
          : submitPairwiseAnswer(answerData));

        if (!resp.ok) throw new Error("Failed to save response");
        setRetryResponse(null);

        if (isExternal && resp.phase === "complete") {
          onComplete();
          return;
        }
        if (!isExternal && resp.phase && resp.phase !== "personalized_pairwise") {
          onPhaseChange(resp.phase, sessionId);
          return;
        }

        const nextTrial = Number(resp.progress?.completed ?? resp.counts?.trials ?? (trialCount + 1));

        setTrialCount(nextTrial);
        if (!isExternal && Number.isInteger(Number(resp.maxTrials))) setMaxTrials(Number(resp.maxTrials));
        await loadNextPair(sessionId);
      } catch (err) {
        console.error('[ERROR] Failed to save pairwise answer after retries:', err);
        setRetryResponse(answerData);
        setSubmitError("We couldn’t save that answer. Retry the same response.");
      } finally {
        submissionLockRef.current = false;
        setIsSubmitting(false);
      }
    },
    [isExternal, sessionId, trialCount, loadNextPair, onComplete, onPhaseChange]
  );

  const handleResponse = useCallback((rawAction, cannotJudgeReason = null) => {
    if (
      submissionLockRef.current
      || isSubmitting
      || retryResponse
      || !sessionId
      || !currentPair
    ) return;
    const answerData = {
      sessionId,
      ...(isExternal ? { userId } : {}),
      pairId: currentPair.pairId,
      issuedTrialId: currentPair.issuedTrialId,
      answerToken: currentPair.answerToken,
      rawAction,
      ...(cannotJudgeReason ? { cannotJudgeReason } : {}),
      reactionTimeMs: startTime ? Date.now() - startTime : null,
      ...(isExternal && window.crypto?.randomUUID
        ? { clientResponseId: window.crypto.randomUUID() }
        : {}),
    };
    submitResponse(answerData);
  }, [isExternal, isSubmitting, retryResponse, sessionId, currentPair, startTime, submitResponse, userId]);

  const closeCantJudgeModal = useCallback(() => {
    restoreModalFocusRef.current = true;
    setShowCantChooseModal(false);
  }, []);

  const handleCantJudge = (reason) => {
    closeCantJudgeModal();
    handleResponse("cannot_judge", reason);
  };

  const handleApproximatelyEqual = () => {
    closeCantJudgeModal();
    handleResponse("approximately_equal");
  };

  useEffect(() => {
    if (!showCantChooseModal) return undefined;
    firstReasonRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeCantJudgeModal();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [showCantChooseModal, closeCantJudgeModal]);

  useEffect(() => {
    if (!showCantChooseModal && !isSubmitting && restoreModalFocusRef.current) {
      restoreModalFocusRef.current = false;
      cantJudgeTriggerRef.current?.focus();
    }
  }, [showCantChooseModal, isSubmitting]);

  if (showIntro && !isExternal) {
    return (
      <PageWrapper>
        <div style={{ textAlign: "center" }}>
          <h2>Movie Preferences</h2>
          <p>In this part, you will again compare pairs of movies to help us learn which movies you like.</p>
          <p><strong>As before, for each pair, choose the movie you prefer. Think about your general preferences rather than your current mood.</strong></p>
          <p><strong>Important:</strong> If you do not like either movie, select "I don't like either movie" rather than choosing one simply because you dislike it less.</p>
          <p>If you genuinely cannot choose between the two movies, select "I genuinely can't choose." For example, this may be because you like both movies equally, or because you do not have enough information about one or both movies to make an informed choice.</p>
          <p>There are no right or wrong answers. Please respond based on your own movie preferences.</p>

          <div style={{ marginTop: "30px", display: "flex", justifyContent: "center", gap: "20px" }}>
            <button
              onClick={async () => {
                setShowIntro(false);
                await beginTask();
              }}
              className="submit-button"
              data-testid="pairwise-start-task"
            >
              Start
            </button>
          </div>
        </div>
      </PageWrapper>
    );
  }

  if (isLoading) return <div style={{ marginTop: "40px" }}>Loading...</div>;
  if (loadError) return <div style={{ color: "red", marginTop: "40px" }}>❌ Failed to load pairs.</div>;
  if (!currentPair) return null;

  const moviesToShow = [currentPair.left, currentPair.right];


  return (
    <div
      className="pairwise-task"
      data-testid="pairwise-task"
      aria-busy={isSubmitting}
    >
      <h2 className="pairwise-question">Which movie do you prefer?</h2>

      <div
        style={{ marginBottom: "10px", color: "var(--color-text-secondary)", fontSize: "1rem" }}
        data-testid="pairwise-trial-counter"
      >
        {isExternal
          ? `Final comparisons: ${currentPair.progress?.current ?? (trialCount + 1)} of ${currentPair.progress?.total ?? 20}`
          : `Shown ${trialCount + 1} / ${maxTrials}`}
      </div>

      {submitError && (
        <div
          role="alert"
          data-testid="pairwise-submit-error"
          style={{
            marginBottom: "16px",
            color: "var(--color-error)",
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          {submitError}
          <button
            type="button"
            className="pairwise-retry-button"
            onClick={() => submitResponse(retryResponse)}
            disabled={isSubmitting}
            data-testid="pairwise-retry-response"
          >
            Retry response
          </button>
        </div>
      )}

      <div className={isExternal ? undefined : "pairwise-movie-choices"}
        style={isExternal ? { display: "flex", justifyContent: "center", alignItems: "flex-start", gap: "60px", width: "100%", maxWidth: "900px" } : undefined}>
        {moviesToShow.map((movie, idx) => {
          const side = idx === 0 ? "left" : "right";
          return (
            <div key={movie.id} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", maxWidth: "350px" }}>
              <button
                type="button"
                onClick={() => handleResponse(
                  side === "left" ? "choose_left" : "choose_right"
                )}
                disabled={isSubmitting || !!retryResponse}
                data-testid={`pairwise-choice-${side}`}
                className="pairwise-movie-choice"
              >
                <PairwiseMoviePresentation movie={movie} />
              </button>
            </div>
          );
        })}
      </div>

      {isExternal ? <div className="pairwise-secondary-responses">
        <button
          type="button"
          onClick={() => handleResponse("reject_both")}
          disabled={isSubmitting || !!retryResponse}
          data-testid="pairwise-reject-both"
          className="pairwise-secondary-button"
        >
          {REJECT_BOTH_LABEL}
        </button>
        <button
          type="button"
          onClick={() => setShowCantChooseModal(true)}
          disabled={isSubmitting || !!retryResponse}
          ref={cantJudgeTriggerRef}
          data-testid="pairwise-open-skip-modal"
          className="pairwise-secondary-button"
        >
          I genuinely can’t judge
        </button>
      </div> : <div className="pairwise-secondary-responses pairwise-secondary-responses--personalized">
        <button type="button" onClick={() => handleResponse("reject_both")}
          disabled={isSubmitting || !!retryResponse} data-testid="pairwise-reject-both"
          className="pairwise-secondary-button">I don't like either movie</button>
        <button type="button" onClick={() => setShowCantChooseModal(true)}
          disabled={isSubmitting || !!retryResponse} ref={cantJudgeTriggerRef}
          data-testid="pairwise-open-skip-modal"
          className="pairwise-secondary-button">I genuinely can't choose</button>
      </div>}

      {showCantChooseModal && (
        <div
          className="pairwise-modal-backdrop"
          role="presentation"
          data-testid="pairwise-cannot-judge-modal"
        >
          <div
            className="pairwise-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pairwise-cannot-judge-title"
          >
            <h2 id="pairwise-cannot-judge-title">{isExternal ? "Why can’t you judge?" : "Why can't you choose?"}</h2>

            <div className="pairwise-modal-options">
              {!isExternal && <button
                type="button"
                ref={firstReasonRef}
                onClick={handleApproximatelyEqual}
                data-testid="pairwise-approximately-equal"
              >
                I like both movies equally
              </button>}

              <button
                type="button"
                ref={isExternal ? firstReasonRef : undefined}
                onClick={() => handleCantJudge("insufficient_information_left")}
                data-testid="pairwise-skip-unfamiliar-left"
              >
                {isExternal ? "I do not know enough about the left movie" : "I don't have enough information about the LEFT movie"}
              </button>

              <button
                type="button"
                onClick={() => handleCantJudge("insufficient_information_right")}
                data-testid="pairwise-skip-unfamiliar-right"
              >
                {isExternal ? "I do not know enough about the right movie" : "I don't have enough information about the RIGHT movie"}
              </button>

              <button
                type="button"
                onClick={() => handleCantJudge("insufficient_information_both")}
                data-testid="pairwise-skip-unfamiliar-both"
              >
                {isExternal ? "I do not know enough about both movies" : "I don't have enough information about either movie"}
              </button>

              {isExternal && <button
                type="button"
                onClick={() => handleCantJudge("approximately_equal")}
                data-testid="pairwise-skip-no-preference"
              >
                {APPROXIMATELY_EQUAL_LABEL}
              </button>}

              <button
                type="button"
                onClick={closeCantJudgeModal}
                data-testid="pairwise-skip-cancel"
                className="pairwise-modal-cancel"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
      />
    </div>
  );
};

export default PairwiseMovieTask;
